import React, { useState, useEffect } from 'react';

import { observer } from 'mobx-react';
import useApi from '../hooks/use-api';
import LoaderModal from '../../components/loader';
import ErrorModal from './error';
import openLinkInNewTab from '../../utils/openLinkInNewTab';
import Spinner from './spinner';
import TicketSelectionModal from './tickets';
import SessionSelectionInPage from './sessions-inpage';
import SeatingSelection from './seating-selection';
import TicketAddedToCart from './ticket-added-to-cart';

const ChooseYourPriceSessions = observer(({ store }) => {
  const [iticketEventId, setIticketEventId] = useState(null);
  const [priceAgeId, setPriceAgeId] = useState(null);
  const [showingId, setShowingId] = useState(null);
  const [showingUid, setShowingUid] = useState(null);
  const [settings, setSettings] = useState(null);
  const [sessionConfig, setSessionConfig] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [showSeating, setShowSeating] = useState(false);
  const [ticketsInCart, setTicketsInCart] = useState(0);
  const [showTicketAddedToCart, setShowTicketAddedToCart] = useState(false);
  const [ticketPrice, setTicketPrice] = useState(null);

  const [loaderText, setLoaderText] = useState('Loading sessions');
  const [apiErrorMessage, setApiErrorMessage] = useState(null);
  const [ticketPurchaseError, setTicketPurchaseError] = useState(null);
  const api = useApi();

  const closeModal = (errorMessage) => {
    store.setLoading(false);
    store.setChosenSession(null);
    store.setTickets(false);
    setShowSeating(false);
    setShowTicketAddedToCart(false);
    if (errorMessage) {
      const body = document.getElementsByTagName('body')[0];
      body.classList.remove('overlay-open');
      // don't reload page if cart was not updated (e.g. Error Modal component rendered etc)
      return;
    }
    // todo: dynamically update cart icon in header when modal closes, rather than reloading page
    location.reload();
    // todo: remove selected seats from cart if thats a thing
    // api.clearCart();
  };

  useEffect(() => {
    store.setLoading(true);
    api.getSessions(store.eventId).then(({ apiError, response }) => {
      if (apiError) {
        setApiErrorMessage('We\'re not able to load sessions at this time. Please try again later.');
      } else {
        store.setSessions(response.sessions);
        setSettings(response.settings);
        setIticketEventId(response.iticket_sessions);
      }
      store.setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (store.numTickets && store.chosenTicket) {
      setTicketPrice(store.chosenTicket.ticket_price);

      // Todo: check if this is an allocated seating system
      if (store.hasAllocatedSeating) {
        setShowSeating(true);
        // set a url for the iTicket seating iframe
      } else {
        // @TODO: Add non-allocated-seating to cart
        // setLoaderText('Prepping you for iticket');
        // store.setLoading(true);
        api.purchaseTickets(
          store.eventId,
          store.chosenSession.choose_your_price_showing_id,
          store.numTickets,
          store.chosenTicket
        ).then(({
          apiError,
          response,
        }) => {
          if (apiError) {
            setApiErrorMessage('We were unable to process your ticket purchase. Please try again later');
            store.setLoading(false);
          } else if (response.sold_out) {
            setTicketPurchaseError('Sorry, it looks like you just missed out. Please select another ticket.');
            store.setLoading(false);
          } else if (response.ticket_limit_reached) {
            setApiErrorMessage((<>You've reached your ticket limit for this session. Return to your <a href={response.iticket_cart}>iTicket cart</a>.</>));
            store.setLoading(false);
          } else {
            // window.location = response.redirect_to;
            store.setLoading(false);
            setShowTicketAddedToCart(true);
          }
        });
      }
    }
  }, [store.numTickets]);

  useEffect(() => {
    if (store.chosenSession) {
      setLoaderText('Checking ticket options');
      store.setLoading(true);
      setShowingId(store.chosenSession.choose_your_price_showing_id);
      if (store.nonCYPId) {
        api.getNonCYPShowingDetails(store.nonCYPId, store.chosenSession.choose_your_price_showing_id)
          .then(({ apiError, response }) => {
            if (apiError) {
              setApiErrorMessage('We\'re not able to load tickets at this time. Please try again later.');
            } else if (response.available) {
              if (response.tickets && response.tickets.ticket_options) {
                store.setTickets(response.tickets.ticket_options);
              } else if (response.primary_tickets && response.primary_tickets.length) {
                store.setTickets(response.primary_tickets);
              } else {
                store.setTickets(response.tickets);
              }
              setSessionConfig(response.session_config);
              store.setHasAllocatedSeating(response.hasAllocatedSeating);
              setShowValues(response);
            }
            store.setLoading(false);
          });
      } else {
        api.getShowingDetails(store.eventId, store.chosenSession.choose_your_price_showing_id)
          .then(({ apiError, response }) => {
            if (apiError) {
              setApiErrorMessage('We\'re not able to load tickets at this time. Please try again later.');
            } else if (response.available) {
              store.setHasAllocatedSeating(response.hasAllocatedSeating);
              if (response.tickets && response.tickets.ticket_options.length) {
                store.setTickets(response.tickets.ticket_options);
                setShowValues(response);
              } else if (response.primary_tickets && response.primary_tickets.length) {
                store.setTickets(response.primary_tickets);
                setShowValues(response);
              } else {
                setApiErrorMessage('We couldn\'t find any available tickets for this session.');
              }
              setSessionConfig(response.session_config);
            } else {
              setApiErrorMessage('We couldn\'t find any available tickets for this session.');
            }
            store.setLoading(false);
          });
      }
    }
  }, [store.chosenSession]);

  const setShowValues = (response) => {
    const priceAgeIdValue = response?.primary_tickets?.[0]?.priceAgeId || response.tickets?.[0]?.priceAgeId || 0;
    const showingUidValue = response?.primary_tickets?.[0]?.showingUid || response.session_config.showing_uid;
    const sessionIdValue = response.session_config.sessionId;
    setPriceAgeId(priceAgeIdValue);
    setShowingUid(showingUidValue);
    setSessionId(sessionIdValue);
    if (response?.session_config?.ticketsInCart) {
      setTicketsInCart(response.session_config.ticketsInCart);
    }
  };

  const handleTicketPurchase = (numTickets, ticketOption) => {
    store.setNumTickets(numTickets);
    store.setChosenTicket(ticketOption);
  };

  const handleSessionSelection = (session) => {
    if (!session.is_choose_your_price_session && !store.nonCYPId) {
      if (session.booking_url) {
        openLinkInNewTab(session.booking_url, true);
      } else {
        openLinkInNewTab(store.bookingUrl, true);
      }
      window.location = window.location.href;
      // close the modal and reset state... by making ChooseYourPrice return null
      // store.setLoading(false);
      // store.setSessions([]);
      // store.setTickets(false);
    } else {
      store.setChosenSession(session);
    }

    // if (!session.is_choose_your_price_session) {
    //   if (session.booking_url) {
    //     openLinkInNewTab(session.booking_url, true);
    //   } else {
    //     openLinkInNewTab(store.bookingUrl, true);
    //   }
    //   // window.location = window.location.href;
    //   // close the modal and reset state... by making ChooseYourPrice return null
    //   // store.setLoading(false);
    //   // store.setSessions([]);
    //   // store.setTickets(false);
    // } else {
    //   store.setChosenSession(session);
    // }
  };

  let view = 'ticketSelection';
  if (apiErrorMessage) {
    view = 'error';
  } else if (store.isLoading) {
    view = (store.hasSessions > 0) ? 'loadingModal' : 'loading';
  } else if (!showTicketAddedToCart && showSeating) {
    view = 'seatingSelection';
  } else if (showTicketAddedToCart) {
    view = 'ticketAddedToCard';
  } else if (!showSeating && store.hasTickets && store.chosenSession) {
    view = 'ticketSelectionModal';
  }

  const renderModal = (view) => {
    switch (view) {
      case 'error':
        return <ErrorModal errorMessage={apiErrorMessage} closeModal={closeModal} />;
      case 'loading':
        return (
          <div className='text-center'>
            {' '}
            <Spinner altText='Loading' />
            {' '}
          </div>
        );
      case 'loadingModal':
        return <LoaderModal messageText={loaderText} />;
      case 'seatingSelection':
        /* Todo: set a seat selection flag */
        return (
          <SeatingSelection
            closeModal={closeModal}
            eventName={store.eventName}
            session={store.chosenSession}
            sessionId={sessionId}
            ticketsInCart={ticketsInCart}
            numTickets={store.numTickets}
            iticketEventId={iticketEventId}
            showingId={showingId}
            showingUid={showingUid}
            setShowTicketAddedToCart={setShowTicketAddedToCart}
            ticketPrice={ticketPrice}
            priceAgeId={priceAgeId}
          />);
      case 'ticketAddedToCard':
        return (
          <TicketAddedToCart
            closeModal={closeModal}
            numTickets={store.numTickets}
            goToCartBtnText={store.goToCartBtnText}
            cartPageUrl={store.cartPageUrl}
            continueBtnText={store.continueBtnText}
            cartExpiryText={store.cartExpiryText}
          />
        );
      case 'ticketSelectionModal':
        return (
          <TicketSelectionModal
            eventName={store.eventName}
            tickets={store.getTickets}
            settings={settings}
            session={store.chosenSession}
            sessionConfig={sessionConfig}
            handleTicketPurchase={handleTicketPurchase}
            closeModal={closeModal}
            ticketPurchaseError={ticketPurchaseError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderModal(view)}
      {store.hasSessions > 0 && (
        <SessionSelectionInPage
          handleSessionSelection={handleSessionSelection}
          sessions={store.sessions}
        />
      )}
    </>
  );
});

export default ChooseYourPriceSessions;
